import RecordedChampion from '../../components/RecordedChampion.jsx'
import { useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { championshipsFor, commandCenterHref, bySoonest } from '../../lib/bodyChampionships.js'
import { formatDate, sportMeta, contentionMeta, ownerName } from '../../lib/championships.js'
import { schoolsIn, DEPARTURE_SPORTS } from '../../lib/sportAffiliation.js'
import { membersFor } from '../../lib/bodyMembers.js'
import { splitPursuable, LAUNCH_DATE } from '../../lib/pursuable.js'
import SchoolDrawer from './SchoolDrawer.jsx'
import { RecentLandings } from '../../components/AgentBadge.jsx'
import { recentCandidateIds } from '../../lib/agentStamp.js'
import styles from './BodyChampionships.module.css'

/**
 * WHAT THIS BODY ACTUALLY CROWNS — the drill-in behind a Governing Bodies card.
 *
 * Brian: "i should be able to see how many championships there are and the
 * interconnectedness — we should be able to click on them and see when they are
 * and who they are with then in the command center."
 *
 * Three asks, and they are three different things:
 *   · HOW MANY   → the count, on the card, for college as well as high school
 *   · WHEN       → the event date, on every row, soonest first
 *   · WHO WITH   → the champion, and the rep who owns it
 *   · …THEN IN THE COMMAND CENTER → a link that arrives FILTERED to this body
 *
 * ⚠️ IT IS A READ. No crown, no correction, no assignment — those live in the
 * Command Center where the whole action console and its permission checks
 * already are. Rebuilding a write here would be a second copy of a governed
 * path, and the second copy is always the one that drifts.
 */
export default function BodyChampionships({ entity, universe, onClose }) {
  const [q, setQ] = useState('')
  const [only, setOnly] = useState('all')
  /* ⚠️ 'body' IS THE DEFAULT AND THE WHOLE STATE IS A CHOICE.
     Brian: "if we just want to show a state - that should be a selection."
     Opening NCHSAA used to show every North Carolina championship, NCISAA's
     included — two associations, two member lists, two sales conversations,
     one undifferentiated count. */
  const [scope, setScope] = useState('body')
  /* ⚠️ THE TEAM SEARCH. Brian: "when a governing body is able to be opened, we
     should be able to search just the teams that compete in that governing
     body… think parent child, grandchild." A conference list of 1,566 UIL
     schools with no filter is not a list, it is a wall. */
  const [teamQ, setTeamQ] = useState('')
  /* ⚠️ AND THE GRANDCHILD. SchoolDrawer has existed unrouted this whole time —
     built, complete, reachable from nowhere. Tapping a member opens it, which
     is the third level: body → team → that team's sports, titles and coaches. */
  const [school, setSchool] = useState(null)

  const stats = useMemo(() => championshipsFor(entity, universe, { scope }), [entity, universe, scope])
  /* Shown on the toggle so the difference between the two is visible BEFORE
     clicking — if they are equal, this state has one association and the choice
     does not matter. */
  const stateStats = useMemo(
    () => (entity?.state ? championshipsFor(entity, universe, { scope: 'state' }) : null),
    [entity, universe],
  )

  /* ⚠️ WHO CAN ACTUALLY WIN THESE TITLES.
     Brian: "if we dont have a clear understanding of team or school or sport
     then WE ARENT GOING to be able to properly assign a championship."
     The championship is the product; the SCHOOL is the customer. Without the
     membership beside the titles they are two unconnected lists. */
  /* ⚠️ ONE SOURCE. This read `membersOf` from conferenceMembers.js — a
     hand-written file covering SEVEN conferences — while sportAffiliation.js
     held 237 schools across thirty. So the Big Ten card showed members and the
     Sun Belt, MAC, CAA and A-10 cards showed NOTHING, which is exactly what
     Brian saw. Two sources for one fact, and the smaller one was wired up.
     conferenceMembers.js is now unused; sportAffiliation is the source. */
  /* ⚠️ ONE SOURCE, AND IT MUST BE THE SAME ONE THE CARD USES. This called
     `schoolsIn` — the older sportAffiliation layer — while the card behind it
     had moved to `membersFor`, so a body could show 15 members on the card and
     a different number in the drawer. Two answers to one question is the
     defect this file already fixed once; it came back when the card changed
     and the drawer did not. */
  const members = useMemo(() => {
    const lvl = entity?.level || (entity?.st ? 'hs' : 'college')
    const viaBody = membersFor(entity || {}, lvl)
    if (viaBody.members?.length) return viaBody.members
    /* Canada and anything the harvest has not reached still answer here. */
    return schoolsIn(entity?.name) || []
  }, [entity])

  const shownMembers = useMemo(() => {
    const t = teamQ.trim().toLowerCase()
    if (!t) return members
    return members.filter((m) => `${m.name} ${m.state || ''} ${m.athleticName || ''}`.toLowerCase().includes(t))
  }, [members, teamQ])

  /* ⚠️ WHAT IS STILL WINNABLE, not what we hold.
     Brian: "i do not want the old championships being included in the count
     like the 2025 or 2026 nba championship. they are already spoken for."
     A total that counts decided titles is a number a rep cannot work. */
  const live = useMemo(() => splitPursuable(stats.rows || stats.list || []), [stats])

  /* ⚠️ THE SPORT-LEVEL ARRIVALS — schools this conference crowns in a sport
     that is NOT their primary conference. Johns Hopkins is a Centennial
     (Division III) school that wins the BIG TEN lacrosse title; Navy plays
     American football and Patriot League everything else. A membership list
     alone cannot express either, and a rep who cannot see them will file the
     championship under the wrong school. */
  const arrivals = useMemo(() => {
    if (!entity?.name) return []
    const out = []
    for (const s of members) out.push(s.name)
    const extra = []
    /* Every sport that HAS a departure anywhere, read from the data rather than
       a list typed here — so a new departure sport appears with no code change. */
    for (const sport of DEPARTURE_SPORTS) {
      for (const s of schoolsIn(entity.name, sport)) {
        if (!out.includes(s.name)) extra.push({ ...s, sport })
      }
    }
    return extra
  }, [entity, members])

  const rows = useMemo(() => {
    const term = q.trim().toLowerCase()
    let list = stats.rows
    if (only === 'undated') list = list.filter((c) => !c.effDate)
    if (only === 'uncrowned') list = list.filter((c) => !c.champion && !c.winningSchool)
    if (only === 'crowned') list = list.filter((c) => c.champion || c.winningSchool)
    if (term) {
      list = list.filter((c) => `${c.name} ${c.sport} ${c.champion || ''}`.toLowerCase().includes(term))
    }
    return [...list].sort(bySoonest)
  }, [stats.rows, q, only])

  /* The records whose latest landing the activity strip asks about. Above the
     early return: hooks may not be conditional. */
  const recentIds = useMemo(() => recentCandidateIds(stats.rows || [], 25), [stats.rows])
  const nameById = useMemo(() => new Map((stats.rows || []).map((c) => [String(c.id), c.name])), [stats.rows])

  if (!entity) return null

  /* ⚠️ PAGED, AND THE CAP IS REPORTED. A body can hold 1,100 records (the CIF
     does). A list that quietly stops is a list that lies about the size of the
     work — the same rule the Stages board learned when "+3,966 more" was a
     dead end. */
  const CAP = 150
  const shown = rows.slice(0, CAP)

  return createPortal(
    <div className={styles.overlay} onClick={onClose}>
      <aside className={styles.panel} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true"
        style={{ '--accent': entity.accent || 'var(--accent)' }}>
        <div className={styles.head}>
          <div>
            <p className={styles.eyebrow}>{entity.acronym}</p>
            <h2 className={styles.title}>{entity.name}</h2>
          </div>
          <button className={styles.close} onClick={onClose} aria-label="Close">✕</button>
        </div>

        {/* THE INTERCONNECTEDNESS, stated as numbers you can act on. Each is a
            filter, because a count you can open is the whole difference. */}
        <div className={styles.stats}>
          <button type="button" data-on={only === 'all' ? '1' : '0'} onClick={() => setOnly('all')}>
            <b>{stats.total.toLocaleString()}</b><span>championships</span>
          </button>
          <button type="button" data-on={only === 'crowned' ? '1' : '0'} onClick={() => setOnly('crowned')}>
            <b>{stats.crowned.toLocaleString()}</b><span>crowned</span>
          </button>
          <button type="button" data-on={only === 'uncrowned' ? '1' : '0'} onClick={() => setOnly('uncrowned')}>
            <b data-bad={stats.uncrowned ? '1' : '0'}>{stats.uncrowned.toLocaleString()}</b><span>no champion</span>
          </button>
          <button type="button" data-on={only === 'undated' ? '1' : '0'} onClick={() => setOnly('undated')}>
            <b data-bad={stats.undated ? '1' : '0'}>{stats.undated.toLocaleString()}</b><span>no date</span>
          </button>
          <div><b>{stats.sports.toLocaleString()}</b><span>sports</span></div>
        </div>

        {/* RECENT ACTIVITY - who landed the latest change on this body's most
            recently dated records: Jarvis (Claude), Dot (ChatGPT), a person,
            or Unstamped. Server-stamped (Brian 2026-10-26). Capped at 25
            records by the route, and said so. */}
        <details className={styles.landings}>
          <summary>Recent activity: who landed it</summary>
          <RecentLandings recordIds={recentIds} nameOf={(id) => nameById.get(String(id)) || null}
            empty="No change has been recorded on these records yet." />
          <p className={styles.landingsNote}>
            The latest landing on each of this body&apos;s {recentIds.length} most recently dated records.
          </p>
        </details>

        {/* ⚠️ THE NUMBER A REP CAN ACTUALLY WORK. Everything above counts the
            object; this counts the opportunity. Shown beside the total rather
            than replacing it, because a figure with no denominator cannot be
            sanity-checked. */}
        {live.total > 0 && live.spokenFor > 0 && (
          <p className={styles.warn}>
            <b>{live.pursuable.toLocaleString()}</b> of {live.total.toLocaleString()} are still winnable.
            {' '}{live.spokenFor.toLocaleString()} {live.spokenFor === 1 ? 'is' : 'are'} spoken for — already
            crowned, or decided before the {LAUNCH_DATE} launch.
          </p>
        )}

        {/* Only where it is a real finding — a state body crowns one champion
            per title, so this would be permanent noise on 51 of the cards. */}
        {stats.ambiguous > 0 && (
          <p className={styles.warn}>
            {stats.ambiguous.toLocaleString()} of these do not say whether they are the
            <b> regular-season</b> or the <b>tournament</b> title. A conference crowns both, and
            they are usually different schools.
          </p>
        )}

        {/* THE SCOPE SELECTION. Only offered where it means something: a
            conference has no state, and a state with one association would be
            two buttons showing the same list. */}
        {stateStats && stateStats.total !== stats.total && (
          <div className={styles.scopeBar}>
            <button type="button" data-on={scope === 'body' ? '1' : '0'} onClick={() => setScope('body')}>
              {entity.acronym} only
            </button>
            <button type="button" data-on={scope === 'state' ? '1' : '0'} onClick={() => setScope('state')}>
              All of {entity.state} · {stateStats.total.toLocaleString()}
            </button>
          </div>
        )}

        {arrivals.length > 0 && (
          <div className={styles.arrivals}>
            <span className={styles.arrivalsHead}>Also crowned here, in one sport only</span>
            {arrivals.map((a) => (
              <span key={`${a.name}-${a.sport}`} className={styles.member} data-affiliate="1">
                {a.name}
                <span className={styles.memberState}>{a.state}</span>
                <span className={styles.memberOnly}>{a.sport} only</span>
              </span>
            ))}
          </div>
        )}

        {members.length > 0 && (
          <details className={styles.members} open>
            <summary>{members.length} member schools — who can win these titles</summary>
            {/* Search scoped to THIS body's members only — the point of the
                parent/child split. UIL has 1,566 schools; an unfiltered list
                of them is a wall, not a roster. */}
            {members.length > 12 && (
              <input className={styles.search} placeholder={`Search the ${members.length} teams in ${entity.acronym}…`}
                value={teamQ} onChange={(e) => setTeamQ(e.target.value)} />
            )}
            <div className={styles.memberList}>
              {shownMembers.map((m) => (
                <button type="button" key={m.name} className={styles.member} data-affiliate={m.only ? '1' : '0'}
                  onClick={() => setSchool(m)}>
                  {m.name}
                  <span className={styles.memberState}>{m.state}</span>
                  {/* ⚠️ A SPORT-SPECIFIC AFFILIATE SAYS SO. Notre Dame is an ACC
                      school in everything but football; Army and Navy are
                      Patriot League in everything but football. Rendering them
                      as plain members would let either be recorded as champion
                      of a title they cannot win. */}
                  {m.only && <span className={styles.memberOnly}>{m.only}</span>}
                </button>
              ))}
              {!shownMembers.length && <span className={styles.memberNone}>No team matches “{teamQ}”.</span>}
            </div>
          </details>
        )}

        <div className={styles.controls}>
          <input className={styles.search} placeholder="Find a championship, sport or champion…"
            value={q} onChange={(e) => setQ(e.target.value)} />
          <a className="btn btn-primary" href={commandCenterHref(entity, { scope })}>Open in Command Center</a>
        </div>

        <div className={styles.list}>
          {shown.map((c) => {
            const sport = sportMeta(c.sport)
            const cont = contentionMeta(c)
            const owner = ownerName(c)
            return (
              <a key={c.id} className={styles.row} href={`/command-center?record=${encodeURIComponent(c.id)}`}
                style={{ '--sport': sport.color }}>
                <span className={styles.rowMain}>
                  <span className={styles.rowName}>{c.name}</span>
                  <span className={styles.rowMeta}>
                    <span className={styles.dot} style={{ background: sport.color }} />
                    {c.sport || 'Sport not set'}
                    {c.division ? ` · ${c.division}` : ''}
                  </span>
                </span>
                {/* WHEN. An undated record says so rather than rendering an
                    epoch-null date as 1970, which is how ~1,241 records once
                    read as having happened. */}
                <span className={styles.when} data-gap={c.effDate ? '0' : '1'}>
                  {c.effDate ? formatDate(c.effDate) : 'No date'}
                </span>
                {/* WHO IT IS WITH — the champion, then the rep who owns it. */}
                <span className={styles.who}>
                  <RecordedChampion record={c} />
                  {!(c.champion || c.winningSchool) && <span className={styles.gap} style={{ color: cont.color }}>{cont.label}</span>}
                  <span className={styles.owner} data-gap={owner ? '0' : '1'}>{owner || 'Unassigned'}</span>
                </span>
              </a>
            )
          })}
        </div>

        {rows.length === 0 && (
          <p className={styles.muted}>
            {stats.total === 0
              /* ⚠️ ZERO IS A CLAIM ABOUT THE DATA and is usually wrong — so it
                 says which join was tried rather than implying none exist. */
              ? `No records name ${entity.acronym}. A body is matched on ITSELF — its name or acronym on the record — not on its state, so a title filed under a different spelling will not appear here.${stateStats?.total ? ` ${entity.state} holds ${stateStats.total.toLocaleString()} in total; use the state view above.` : ''}`
              : `Nothing matches that filter — ${stats.total.toLocaleString()} in total.`}
          </p>
        )}
        {rows.length > CAP && (
          <p className={styles.muted}>Showing {CAP} of {rows.length.toLocaleString()} — filter, or open the Command Center for the full set.</p>
        )}
      </aside>

      {/* THE GRANDCHILD: body → team → that team's sports, titles and coaches. */}
      {school && <SchoolDrawer school={school} onClose={() => setSchool(null)} />}
    </div>,
    document.body,
  )
}
